import { addDays } from 'date-fns';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import * as z from 'zod';
import { VAIR_MAX_PLAYERS, VAIR_MINIMUM_PLAYERS } from 'constants/vair';
import { EventGroupFormatsEnum, ScoringFormatEnum } from 'types/generated/client';

export enum QuickCreateSteps {
  CollectPayments = 'COLLECT_PAYMENTS',
  WhenAndWhere = 'WHEN_AND_WHERE',
  Events = 'EVENTS',
  ParticipantsAndGames = 'PARTICIPANTS_AND_GAMES',
  Success = 'SUCCESS',
}

// Which VAIR entry the user came through (drives the SelectVairFormat step).
// Set via the `?vairFlow=` query param on the quick-create page.
export type VairFlow = 'integrated-event' | 'rating-session';

// Format chosen on the SelectVairFormat step.
//  - tournament    → existing non-VAIR flow (round robin → single elim)
//  - traditional-rr → existing non-VAIR flow + VAIR-required toggle
//  - vair-rr        → existing VAIR flow (fixed rounds by registration count)
//  - flexible       → new "VAIR Free format" screen (pending)
export const VAIR_FORMAT_OPTIONS = [
  'tournament',
  'traditional-rr',
  'vair-rr',
  'flexible',
] as const;
export type VairFormatOption = (typeof VAIR_FORMAT_OPTIONS)[number];

export type ButtonTogglerProps = {
  disabled?: boolean;
  type: 'submit' | 'button' | 'reset';
  prevStep?: () => void;
  backTitle?: string;
  nextStep?: () => void;
  nextTitle: string;
  currentStep?: number;
  isLoading: boolean;
  buttonClassName?: string;
};
export type EventItem = {
  doubles: string;
  singles: string;
  activeTab: number;
  eventId: number;
};
// Infer types from schemas
export type WhenAndWhereData = z.infer<typeof whenAndWhereSchema>;
export type EventsData = {
  maxEvents: number;
  events: EventItem[];
};
export type ParticipantsAndGamesData = {
  eventId: number;
  maxTeams: number;
  minGames: number;
  eventTitle: string;
  eventTab: string;
};

export type ParticipantDataType = {
  participantsAndGames: ParticipantsAndGamesData[];
};

// Define the FormData interface
export interface FormData {
  eventId: string;
  whenAndWhere: WhenAndWhereData;
  events: EventsData;
  participantsAndGames: ParticipantsAndGamesData[];
}
type FormDataUpdater = (prevData: FormData) => FormData;

// Define the props for QuickCreateFormTypes
export type QuickCreateFormTypes = {
  formData?: FormData;
  setFormData?: (updater: FormDataUpdater) => void;
  nextStep?: () => void;
  prevStep?: () => void;
  handleSubmitData?: (formData: FormData) => void;
  // New optional props for edit mode (backwards compatible)
  mode?: 'create' | 'edit'; // Default: 'create'
  onUpdate?: (data: WhenAndWhereData) => Promise<void>; // For edit flow
  eventId?: string; // Existing event ID for edit
  initialData?: WhenAndWhereData; // Prepopulated data for edit mode (cleaner than formData)
  className?: string; // Optional wrapper className for styling
};

export const venueSchema = z.object({
  id: z.string(),
  address: z
    .string()
    .min(
      1,
      `You must be hosting at a valid location. If your location isn't listed, email us at team@bounce.game`,
    ),
  latitude: z.number(),
  longitude: z.number(),
  venueImageURL: z.string(),
  city: z.any(),
  timezone: z.string().nullable().optional(),
});

export const groupSchema = {
  teamLimit: z.number().default(12),
  priceUnitAmount: z.union([z.number().nonnegative().min(0), z.nan()]).optional(),
  // gender: z.nativeEnum(CompetitionGenderEnum),
  // teamType: z.nativeEnum(TeamTypesEnum),
  minimumAge: z.union([z.number().nonnegative().min(0), z.nan()]).optional(),
  maximumAge: z.union([z.number().nonnegative().min(0).max(100), z.nan()]).optional(),
  minimumRating: z.union([z.number().nonnegative().min(0), z.nan()]).optional(),
  maximumRating: z.union([z.number().nonnegative().min(0), z.nan()]).optional(),
  isAgeRestricted: z.boolean(),
  isRatingRequired: z.boolean(),
};

export const zodGroupSchema = z.object(groupSchema);

export const timeSlotSchema = {
  timeSlotDate: z.date(),
  timeSlotFrom: z.number(),
  timeSlotTo: z.number(),
};

export const zodTimeSlotSchema = z.object(timeSlotSchema);

export const eventGroupSchema = z.object({
  ...groupSchema,
  ...timeSlotSchema,
  format: z.nativeEnum(EventGroupFormatsEnum),
  minimumNumberOfGames: z.number().nonnegative('Minimum games number cannot be negative'),
  scoringFormat: z.nativeEnum(ScoringFormatEnum),
  gamesPerMatch: z.number().nonnegative('Games per match cannot be negative'),
  totalPoints: z.number().nonnegative('Total points cannot be negative'),
  winBy: z.number().nonnegative('Win by cannot be negative'),
});

export const VAIR_SCORE_OPTIONS = [9, 11, 15] as const;
export const DEFAULT_VAIR_SCORE_LIMIT = VAIR_SCORE_OPTIONS[1] as number;

export const whenAndWhereSchema = z.object({
  title: z.string().min(1, { message: 'Title is required' }),
  selectDate: z
    .date()
    .default(new Date())
    .refine(
      (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date >= today;
      },
      {
        message: 'Date cannot be in the past',
      },
    ),
  selectStartTime: z
    .number()
    .min(0, { message: 'Select a valid start time' })
    .max(1439, { message: 'Select a valid start time' }),
  venue: venueSchema,
  minCourts: z.number().optional(),
  isVairEvent: z.boolean().default(false),
  vairFormat: z.enum(VAIR_FORMAT_OPTIONS).optional(), // Selected on the SelectVairFormat step
  // "VAIR required" toggle (Traditional Round Robin) → events.is_vair_required.
  // NOT events.is_rating_required, which predates VAIR and drives the DUPR gate.
  isVairRequired: z.boolean().default(false),
  numberOfPlayers: z.number().min(VAIR_MINIMUM_PLAYERS).max(VAIR_MAX_PLAYERS).optional(), // For VAIR events only
  scoreLimit: z.number().default(DEFAULT_VAIR_SCORE_LIMIT), // For VAIR events only: 9, 11, or 15
});

export const eventsSchema = z.object({
  maxEvents: z
    .number()
    .min(1, 'At least 1 event is required')
    .max(20, 'You can add up to 20 events')
    .nonnegative('This field is required'),
  events: z.array(
    z
      .object({
        doubles: z.string().default(''),
        singles: z.string().default(''),
        activeTab: z.number().default(0),
        eventId: z.number(),
      })
      .refine((data) => data.doubles || data.singles, {
        message: 'Title is required.',
        path: ['doubles'],
      }),
  ),
});

export type EventsFormDataSchema = z.infer<typeof eventsSchema>;

export const ParticipantAndGamesArray = z.array(
  z.object({
    eventTab: z.string(),
    eventTitle: z.string(),
    eventId: z.number(),
    maxTeams: z
      .number()
      .min(1, 'At least 1 team is required')
      .max(20, 'You can add up to 20 teams')
      .nonnegative('This field is required'),
    minGames: z
      .number()
      .min(1, 'At least 1 game is required')
      .max(20, 'You can add up to 20 games')
      .nonnegative('This field is required'),
  }),
);

// Define schema for Zod validation
export const participantsAndGamesSchema = z.object({
  participantsAndGames: ParticipantAndGamesArray,
});

export type EventTypeTabProps = {
  control: Control<EventsFormDataSchema>;
  errors: FieldErrors<EventsFormDataSchema>;
  field: EventItem;
  index: number;
  onChange: (index: number, val: number, field: EventItem) => void;
  register: UseFormRegister<EventsFormDataSchema>;
  maxEvents: number;
};

export type SuccessType = {
  formData: FormData;
};
